import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getEducationList,
  getEducationById,
  createEducation,
  updateEducation,
  patchEducation,
  deleteEducation,
} from "../api/education.api";
import type {
  EducationPayload,
  GetEducationListParams,
} from "@/interface/profile";
import { educationKeys, candidateProfileKeys, STALE_5_MIN } from "@/lib/queryKey";

export const useEducationList = (params?: GetEducationListParams) =>
  useQuery({
    queryKey: educationKeys.list(params),
    queryFn: () => getEducationList(params),
    ...STALE_5_MIN,
  });

export const useEducationDetail = (id: string | number, enabled = true) =>
  useQuery({
    queryKey: educationKeys.detail(id),
    queryFn: () => getEducationById(id),
    enabled: Boolean(id) && enabled,
    ...STALE_5_MIN,
  });

export const useCreateEducation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createEducation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: educationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: candidateProfileKeys.education() });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};

export const useUpdateEducation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string | number;
      payload: EducationPayload;
    }) => updateEducation(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: educationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: educationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: candidateProfileKeys.education() });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};

export const usePatchEducation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string | number;
      payload: Partial<EducationPayload>;
    }) => patchEducation(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: educationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: educationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: candidateProfileKeys.education() });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};

export const useDeleteEducation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => deleteEducation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: educationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: candidateProfileKeys.education() });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};
